import React, { Dispatch, SetStateAction } from 'react'
import styled from 'styled-components'
import { get, Box, Button, Space, TextBody } from '@ally/metronome-ui'

import { DataTypeName, DataType } from '../types'
import AddWidgets from './AddWidgets'
import { useCustomizeDashboard } from '../CustomizeDashboardContext/CustomizeDashboardContext'
import ReorderList from '../ReorderList'

const StyledButton = styled(Button)`
  width: 100%;
  height: auto;
  padding: ${get.space('md')} ${get.space('sm')};
  justify-content: flex-start;
  border-radius: 0;
  border: 2px dashed ${get.colors('slate-2')};
  border-radius: 4px;

  /* Metronome v22 override */
  &:hover {
    background-color: ${get.colors('white')};
    box-shadow: none;
  }
`

const ManageWidgets: React.FC<{
  handleRef: (el: HTMLElement | null) => void
  headingRef: React.RefObject<HTMLHeadingElement>
  onListChange: (type: DataTypeName, list: DataType<DataTypeName>[]) => void
  canAddWidgets: boolean
  showAddWidgets: boolean
  setShowAddWidgets: Dispatch<SetStateAction<boolean>>
}> = ({
  handleRef,
  headingRef,
  onListChange,
  canAddWidgets,
  showAddWidgets,
  setShowAddWidgets,
}) => {
  const [{ widgets, customizeDataToPatch, isSaving }] = useCustomizeDashboard()

  const currentWidgets = customizeDataToPatch.widgets ?? widgets

  // currentWidgets + any widgets they added that are pending a save
  const widgetsWithPending = currentWidgets.filter(w => w.enabled)

  function cancelAddWidgets(): void {
    setShowAddWidgets(false)
    // istanbul ignore next
    if (headingRef.current) headingRef.current.focus()
  }

  return (
    <>
      <Space mb="md">
        <TextBody tag="p" size="sm" tabIndex={-1} ref={handleRef}>
          Manage widgets for easy access to the information and tasks that are
          most important to you.
        </TextBody>
      </Space>
      {!showAddWidgets && (
        <Box>
          {/* <OrderWidgets> */}
          {widgetsWithPending.length > 0 ? (
            <ReorderList
              type="widgets"
              list={widgetsWithPending}
              onListChange={onListChange}
            />
          ) : null}
        </Box>
      )}
      {canAddWidgets && !showAddWidgets && (
        <Space mt="md">
          <StyledButton
            variant="outline"
            text="+ Add Widgets"
            data-testid="add-widgets-button"
            onClick={(): void => {
              setShowAddWidgets(true)
              if (headingRef.current) headingRef.current.focus()
            }}
            disabled={isSaving}
            aria-disabled={isSaving}
          />
        </Space>
      )}
      {canAddWidgets && showAddWidgets && (
        <AddWidgets cancelAddWidgets={cancelAddWidgets} />
      )}
    </>
  )
}

export default ManageWidgets
